/* global window */

import videojs from 'video.js';
import get from 'lodash/get';
import some from 'lodash/some';
import inView from 'in-view';
import isMobileJS from 'ismobilejs';
import {
  isStickyActive,
  stickyEnterViewHandler,
  stickyExitViewHandler
} from './sticky';
import { isAdsManagerReady } from './ads';

/**
 * Called when player enters viewport.
 * If video playback has not started yet, will trigger autoplay event.
 * Otherwise will attempt to resume video playback.
 * @param {Object} encore - Encore video player instance.
 */
function enterViewHandler(encore) {
  videojs.log(`${encore.player.id()} entered viewport`);

  if (isStickyActive() && encore.source.canBeSticky) {
    return;
  }

  // Ignore autoplay when a player is in fullscreen
  if (some(window.hearstPlayer.instances, instance => instance.player.isFullscreen())) {
    return;
  }

  if (!encore.inViewPlayTriggered) {
    encore.inViewPlayTriggered = true;
    encore.player.trigger('inViewPlay');
    return;
  }

  if (get(encore.player, 'ima.controller.sdkImpl.adsActive', false)) {
    encore.player.ima.resumeAd();
  } else {
    encore.player.play();
  }
}

/**
 * Called when player exits viewport. Pauses all video playback.
 * @param {Object} encore - Encore video player instance.
 */
function exitViewHandler(encore) {
  videojs.log(`${encore.player.id()} exited viewport`);

  if (isStickyActive() && encore.source.canBeSticky) {
    return;
  }

  window.hearstPlayer.pauseAllInstances();
}

/**
 * Bind all the necessary player events to the queue handler.
 * @param {Object} encore - Encore video player instance.
 */
function inViewPlayHandler(encore) {
  window.hearstPlayer.pauseAllInstances(encore.player.id());

  isAdsManagerReady(encore)
    .then(() => encore.player.play());
}

/**
 * Registers viewport events.
 * If environment is not mobile, not Safari and autoplay flag is true,
 * also registers autoplay event handler to start video playback once
 * video player enters the viewport.
 * @param {Object} encore - Encore video player instance.
 * @param {boolean} canAutoplay - is autoplay allowed.
 * @param {boolean} shouldAutoplay - is autoplay requested.
 */
function registerInViewEvents(encore, canAutoplay, shouldAutoplay) {
  inView(`#${encore.player.id()}`, { threshold: 0.75 })
    .on('enter', enterViewHandler.bind(this, encore))
    .on('exit', exitViewHandler.bind(this, encore));

  inView(`#${encore.player.id()}`, { threshold: 0.75 })
    .on('exit', stickyExitViewHandler.bind(this, encore));

  inView(`#${encore.widgetId}_${encore.embedId}_placeholder`, { threshold: 0.75 })
    .on('enter', stickyEnterViewHandler.bind(this, encore));

  if (isMobileJS.any) {
    return;
  }

  if (!canAutoplay) {
    return;
  }

  if (!shouldAutoplay) {
    return;
  }

  encore.player.on('inViewPlay', inViewPlayHandler.bind(this, encore));
}

/**
 * Attach autoplay events to video player.
 * @param {Object} Encore - Encore video player instance.
 */
function attachAutoplayEvents(encore) {
  encore.player.on('play', () => {
    window.hearstPlayer.pauseAllInstances(encore.playerElementId);
  });

  encore.player.on('ads-ima-resume', () => {
    window.hearstPlayer.pauseAllInstances(encore.playerElementId);
  });

  registerInViewEvents(encore,
    encore.settings.canAutoplay,
    encore.shouldAutoplay);
}

export default attachAutoplayEvents;
